import { Component, inject } from '@angular/core';
import { HeaterPendingAudit } from '@app/models/heaters';
import { TableColumn } from '@app/models/table';
import { ExpandableTableComponent } from '@app/components/shared/expanded-table/expanded-table';
import { Heaters } from '@app/services/heaters/heaters';
import { Filter } from '@app/components/audit-pending/filter/filter';
import { Header } from '@app/components/audit-pending/header/header';
import { EmptyData } from '@app/components/shared/empty-data/empty-data';
import { ExpandedComponent } from '@app/components/audit-pending/expanded-component/expanded-component';
import { Dialog } from '@app/components/shared/dialog/dialog';
import { ModalDeny } from '@app/components/audit-pending/modal-deny/modal-deny';
import { toast } from 'ngx-sonner';
import { Paginator } from '@app/components/shared/paginator/paginator';
import { Loader } from '@app/components/shared/loader/loader';

@Component({
  selector: 'app-pending-audit',
  imports: [
    ExpandableTableComponent,
    Filter,
    Header,
    EmptyData,
    ExpandedComponent,
    Dialog,
    ModalDeny,
    Paginator,
    Loader,
  ],
  templateUrl: './pending-audit.html',
  styleUrl: './pending-audit.css',
})
export class PendingAudit {
  private readonly HeatersService = inject(Heaters);
  showModal: boolean = false;
  actionKey: 'approve' | 'deny' | null = null;
  loading: boolean = true;

  columns: TableColumn<HeaterPendingAudit>[] = [
    {
      key: 'select' as keyof HeaterPendingAudit, // 👈 key virtual
      header: '',
      align: 'center',
      cell: (row) => ({
        type: 'checkbox',
        row,
      }),
    },
    {
      key: 'serial',
      header: 'Serial',
      align: 'center',
    },
    {
      key: 'purchaseDate',
      header: 'Fecha de carga',
      align: 'center',
    },
    {
      key: 'supplier',
      header: 'Proveedor',
      align: 'center',
    },
    {
      key: 'branch',
      header: 'Filial',
      align: 'center',
    },
    {
      key: 'brand',
      header: 'Marca',
      align: 'center',
    },
    {
      key: 'modelReference',
      header: 'Referencia',
      align: 'center',
    },
    {
      key: 'draftType',
      header: 'Tipo de tiro',
      align: 'center',
      cell: (row) => `${row.draftType === 'FORCED' ? 'Forzado' : 'Natural'}`,
    },
    {
      key: 'capacityLiters',
      header: 'Litraje',
      align: 'center',
    },
  ];
  selection: HeaterPendingAudit[] = [];

  data: HeaterPendingAudit[] = [];
  supplierList: string[] = [];
  branchList: string[] = [];

  page: number = 1;
  limit = 10;
  totalItems: number = 0;

  filtered: boolean = false;
  supplier: string = '';
  branch: string = '';
  startDate: string = '';
  endDate: string = '';
  serial: string = '';

  ngOnInit() {
    this.getInventory();
  }

  getInventory() {
    this.filtered = false;
    this.loading = true;
    this.HeatersService.getHeatersInventory(this.page, this.limit).subscribe({
      next: (response) => {
        const data = response.data;
        this.data = data;
        this.supplierList = [...new Set(data.map((item) => item.supplier))];
        this.branchList = [...new Set(data.map((item) => item.branch))];
        this.totalItems = response.total;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getInventoryFiltered({
    supplier,
    branch,
    startDate,
    endDate,
    serial,
  }: {
    supplier: string;
    branch: string;
    startDate: string;
    endDate: string;
    serial: string;
  }) {
    this.supplier = supplier;
    this.branch = branch;
    this.startDate = startDate;
    this.endDate = endDate;
    this.serial = serial;
    this.filtered = true;

    // this.page = 1;
    this.loading = true;
    this.HeatersService.getHeatersInventoryFiltered(
      supplier,
      branch,
      startDate,
      endDate,
      serial,
      this.page,
      this.limit,
    ).subscribe({
      next: (response) => {
        const data = response.data;
        this.data = data;
        this.totalItems = response.total;
        // this.supplierList = [...new Set(data.map((item) => item.supplier))];
        // this.branchList = [...new Set(data.map((item) => item.branch))];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  setSelection(selection: HeaterPendingAudit[]) {
    this.selection = selection;
  }

  handleApproveSingle(row: HeaterPendingAudit) {
    this.selection = [row];
    console.log({ row }, 'approved');

    this.actionKey = 'approve';
    this.showModal = true;
  }

  handleDenySingle(row: HeaterPendingAudit) {
    this.selection = [row];
    console.log({ row }, 'denied');
    this.actionKey = 'deny';
    this.showModal = true;
  }

  handleApproveSelection() {
    console.log({ selection: this.selection });
    this.actionKey = 'approve';
    this.showModal = true;
  }

  handleDenySelection() {
    console.log({ selection: this.selection });
    this.actionKey = 'deny';
    this.showModal = true;
  }

  handleCancelModal() {
    console.log('modal closed');
    this.showModal = false;
    this.actionKey = null;
  }

  handleConfirmApproveSelection() {
    this.HeatersService.updateHeaterStatus(
      this.selection.map((item) => item.id),
      'ACTIVE_IN_PROGRESS',
    ).subscribe({
      next: () => {
        console.log('modal approve confirmed');
        this.showModal = false;
        this.actionKey = null;
        toast.success(
          this.selection.length > 1
            ? `${this.selection.length} calentadores aprobados exitosamente`
            : `Calentador ${this.selection[0].serial} aprobado exitosamente`,
        );
        this.selection = [];
        this.getInventory();
      },
      error: () => {
        toast.error('Error al actualizar el estado de los calentadores');
      },
    });
  }

  handleConfirmDenySelection(comments: string) {
    this.HeatersService.updateHeaterStatus(
      this.selection.map((item) => item.id),
      'REJECTED',
      comments,
    ).subscribe({
      next: () => {
        console.log('modal deny confirmed');
        this.showModal = false;
        this.actionKey = null;
        toast.success(
          this.selection.length > 1
            ? `${this.selection.length} calentadores rechazados exitosamente`
            : `Calentador ${this.selection[0].serial} rechazado exitosamente`,
        );
        this.selection = [];
        this.getInventory();
      },
      error: () => {
        toast.error('Error al actualizar el estado de los calentadores');
      },
    });
  }

  handlePageChange(page: number) {
    this.page = page;
    if (this.filtered) {
      this.getInventoryFiltered({
        supplier: this.supplier,
        branch: this.branch,
        startDate: this.startDate,
        endDate: this.endDate,
        serial: this.serial,
      });
    } else {
      this.getInventory();
    }
  }

  handleLimitChange(limit: number) {
    console.log('new limit', limit);
    this.limit = limit;
    this.page = 1;
    if (this.filtered) {
      this.getInventoryFiltered({
        supplier: this.supplier,
        branch: this.branch,
        startDate: this.startDate,
        endDate: this.endDate,
        serial: this.serial,
      });
    } else {
      this.getInventory();
    }
  }
}
